import { viewHeight, viewWidth } from '@/core/viewport';

/**
 * Follow camera with a deadzone and world clamping.
 *
 * The deadzone stops the camera twitching under every small movement, which is
 * especially bad on a phone where the thumb is never perfectly still. Clamping
 * keeps the void outside the map off-screen.
 */

export interface CameraBounds {
  readonly width: number;
  readonly height: number;
}

export interface CameraOptions {
  /** Half-width/half-height of the region the target may move in freely. */
  readonly deadzoneX?: number;
  readonly deadzoneY?: number;
  /** Fraction of remaining distance closed per second. 1 = instant. */
  readonly smoothing?: number;
}

export class Camera {
  x = 0;
  y = 0;

  private readonly deadzoneX: number;
  private readonly deadzoneY: number;
  private readonly smoothing: number;

  constructor(options: CameraOptions = {}) {
    // A camera that trails the character reads as input lag even when the
    // character itself is responding immediately, so the deadzone is under a
    // tile and the easing is quick. Enough smoothing remains to absorb thumb
    // jitter without the view feeling bolted to Katie.
    this.deadzoneX = options.deadzoneX ?? 14;
    this.deadzoneY = options.deadzoneY ?? 9;
    this.smoothing = options.smoothing ?? 20;
  }

  /** Snap directly to the target, for spawns and teleports. */
  snapTo(targetX: number, targetY: number, bounds: CameraBounds): void {
    const desired = clampToBounds(
      targetX - viewWidth() / 2,
      targetY - viewHeight() / 2,
      bounds,
    );
    this.x = desired.x;
    this.y = desired.y;
  }

  follow(targetX: number, targetY: number, bounds: CameraBounds, dt: number): void {
    const centreX = this.x + viewWidth() / 2;
    const centreY = this.y + viewHeight() / 2;

    let desiredX = this.x;
    let desiredY = this.y;

    const offsetX = targetX - centreX;
    if (Math.abs(offsetX) > this.deadzoneX) {
      desiredX += offsetX - Math.sign(offsetX) * this.deadzoneX;
    }

    const offsetY = targetY - centreY;
    if (Math.abs(offsetY) > this.deadzoneY) {
      desiredY += offsetY - Math.sign(offsetY) * this.deadzoneY;
    }

    const clamped = clampToBounds(desiredX, desiredY, bounds);

    // Exponential smoothing, framerate-independent: the same easing regardless
    // of whether a step is 16ms or 8ms.
    const t = 1 - Math.exp(-this.smoothing * dt);
    this.x += (clamped.x - this.x) * t;
    this.y += (clamped.y - this.y) * t;
  }
}

/**
 * Clamp so the view never shows outside the map. Maps smaller than the viewport
 * are centred instead, which would otherwise clamp to a negative range and jitter.
 */
export function clampToBounds(
  x: number,
  y: number,
  bounds: CameraBounds,
): { x: number; y: number } {
  const maxX = bounds.width - viewWidth();
  const maxY = bounds.height - viewHeight();

  return {
    x: maxX <= 0 ? maxX / 2 : Math.min(Math.max(x, 0), maxX),
    y: maxY <= 0 ? maxY / 2 : Math.min(Math.max(y, 0), maxY),
  };
}
